'use client'

/**
 * InlineEmailCapture — the missing low-friction funnel rung. Captures an engaged visitor's email with NO
 * account required (posts to /api/subscribe → email_leads), turning anonymous Google traffic into an
 * owned audience. Proof-backed (embeds SubscribeProof) so the ask lands. Shown to everyone; hides itself
 * after a successful submit on this device (localStorage). The heavier "create a free account" CTA still
 * lives at the bottom of these pages — this is the lighter first step for people who won't sign up yet.
 */

import { useEffect, useState } from 'react'
import { Mail, ArrowRight, Check } from 'lucide-react'
import SubscribeProof from './SubscribeProof'

const DONE_KEY = 'te_email_subscribed'

export default function InlineEmailCapture({
  source,
  headline = 'Get the best catches in your inbox',
  subhead = 'Free daily email of the top plays and unusual-volume alerts our scanner flags. No account needed.',
  showProof = true,
}: {
  source: string
  headline?: string
  subhead?: string
  showProof?: boolean
}) {
  const [email, setEmail] = useState('')
  const [hp, setHp] = useState('') // honeypot
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    try { if (localStorage.getItem(DONE_KEY)) setHidden(true) } catch {}
  }, [])

  if (hidden) return null

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (status === 'loading') return
    const value = email.trim()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) { setStatus('error'); return }
    setStatus('loading')
    try {
      const path = typeof window !== 'undefined' ? window.location.pathname : ''
      const r = await fetch('/api/subscribe', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: value, source, path, hp }),
      })
      if (r.ok) {
        setStatus('done')
        try { localStorage.setItem(DONE_KEY, '1') } catch {}
      } else {
        setStatus('error')
      }
    } catch {
      setStatus('error')
    }
  }

  return (
    <section className="my-8 overflow-hidden rounded-2xl border border-amber-300 dark:border-amber-500/25 bg-amber-50 dark:bg-transparent dark:bg-gradient-to-b dark:from-amber-500/[0.07] dark:to-zinc-900/40 p-5">
      {status === 'done' ? (
        <div className="flex items-center gap-3 py-1">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-500/15">
            <Check className="h-5 w-5 text-emerald-700 dark:text-emerald-400" strokeWidth={3} />
          </span>
          <div>
            <div className="text-[15px] font-bold text-foreground">You&apos;re on the list.</div>
            <div className="text-[13px] text-muted-foreground">We&apos;ll send the top plays and biggest catches straight to your inbox.</div>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-3 flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-500/15">
              <Mail className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </span>
            <div className="min-w-0">
              <h3 className="text-[15px] font-black leading-tight text-foreground sm:text-base">{headline}</h3>
              <p className="mt-0.5 text-[13px] leading-snug text-muted-foreground">{subhead}</p>
            </div>
          </div>

          <form onSubmit={submit} className="flex flex-col gap-2 sm:flex-row">
            {/* Honeypot: hidden from users, bots fill it → server drops the submit. */}
            <input
              type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)}
              className="hidden" aria-hidden="true" name="company"
            />
            <input
              type="email" inputMode="email" autoComplete="email" required
              aria-label="Email address"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('idle') }}
              placeholder="you@email.com"
              className="min-w-0 flex-1 rounded-lg border border-border bg-background px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none transition focus:border-amber-300 dark:focus:border-amber-500/60"
            />
            <button
              type="submit" disabled={status === 'loading'}
              className="flex shrink-0 items-center justify-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-bold text-zinc-950 transition hover:bg-amber-400 disabled:opacity-60"
            >
              {status === 'loading' ? 'Adding…' : <>Get free alerts <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>
          {status === 'error' && <p className="mt-1.5 text-[12px] text-rose-600 dark:text-rose-400">Enter a valid email and try again.</p>}
          <p className="mt-2 text-[11px] text-muted-foreground">No spam. Unsubscribe anytime. Not investment advice.</p>

          {showProof && <div className="mt-4 border-t border-border pt-4"><SubscribeProof compact /></div>}
        </>
      )}
    </section>
  )
}
